import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { StrategyExplanation } from './StrategyExplanation';
import { TicketOptimizer } from './TicketOptimizer';
import { RoundCalculations } from './RoundCalculations';
import { ResultsDisplay } from './ResultsDisplay';
import { formatCurrency } from '@/utils/formatters';

const TICKET_PRICES = [
  { price: 2299, capital: 500000 },
  { price: 1249, capital: 200000 },
  { price: 649, capital: 100000 },
  { price: 379, capital: 50000 },
  { price: 269, capital: 25000 },
  { price: 159, capital: 10000 },
];

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000];

export const ProfitCalculator = () => {
  const [investmentAmount, setInvestmentAmount] = useState<string>('2000');

  const optimizeTicketPurchase = (amount: number) => {
    let remainingAmount = amount;
    const purchasedTickets: Array<{ price: number; capital: number; quantity: number }> = [];
    let totalCapital = 0;

    for (const ticket of TICKET_PRICES) {
      const quantity = Math.floor(remainingAmount / ticket.price);
      if (quantity > 0) {
        purchasedTickets.push({
          ...ticket,
          quantity
        });
        remainingAmount -= quantity * ticket.price;
        totalCapital += quantity * ticket.capital;
      }
    }

    return {
      purchasedTickets,
      remainingAmount,
      totalCapital,
      totalSpent: amount - remainingAmount
    };
  };

  const calculateFourRounds = (initialInvestment: number) => {
    const rounds = [];
    let totalWithdrawals = 0;
    let totalReinvestments = 0;
    const botFundsHistory: number[] = [];
    
    const initialTicketOptimization = optimizeTicketPurchase(initialInvestment);
    
    for (let round = 1; round <= 4; round++) {
      let ticketInvestment = 0;
      let capitalAllocated = 0;
      let aiPropProfit = 0;
      let withdrawal = 0;
      let reinvestment = 0;
      let botFunds = 0;
      let botProfit = 0;
      
      if (round === 1) {
        ticketInvestment = initialTicketOptimization.totalSpent;
        capitalAllocated = initialTicketOptimization.totalCapital;
        
        aiPropProfit = capitalAllocated * 0.05 * 0.8;
        
        withdrawal = aiPropProfit * 0.1;
        reinvestment = aiPropProfit * 0.3;
        botFunds = aiPropProfit * 0.6;
        
        botFundsHistory.push(botFunds);
        
        botProfit = 0;
        
      } else {
        const previousRound = rounds[round - 2];
        ticketInvestment = previousRound.reinvestment;
        
        if (ticketInvestment > 0) {
          const ticketOptimization = optimizeTicketPurchase(ticketInvestment);
          capitalAllocated = ticketOptimization.totalCapital;
          
          aiPropProfit = capitalAllocated * 0.05 * 0.8;
          
          withdrawal = aiPropProfit * 0.1;
          reinvestment = aiPropProfit * 0.3;
          botFunds = aiPropProfit * 0.6;
          
          botFundsHistory.push(botFunds);
          
        } else {
          aiPropProfit = 0;
          withdrawal = 0;
          reinvestment = 0;
          botFunds = 0;
          botFundsHistory.push(0);
        }
        
        botProfit = 0;
        for (let i = 0; i < round - 1; i++) {
          const previousBotFunds = botFundsHistory[i];
          if (previousBotFunds > 0) {
            botProfit += previousBotFunds * 0.1 * 3;
          }
        }
      }
      
      const totalAssets = aiPropProfit + botProfit - reinvestment;
      
      totalWithdrawals += withdrawal;
      totalReinvestments += reinvestment;
      
      rounds.push({
        round,
        ticketInvestment,
        capitalAllocated,
        aiPropProfit,
        withdrawal,
        reinvestment,
        botFunds,
        botProfit,
        totalAssets,
        cumulativeWithdrawals: totalWithdrawals,
        cumulativeReinvestments: totalReinvestments,
        botFundsHistory: [...botFundsHistory]
      });
    }
    
    return rounds;
  };

  const calculationResults = useMemo(() => {
    const amount = parseFloat(investmentAmount) || 0;
    if (amount <= 0) return null;

    const initialTicketOptimization = optimizeTicketPurchase(amount);
    
    const rounds = calculateFourRounds(amount);
    
    const totalAssetsFromRounds = rounds.reduce((sum, round) => sum + round.totalAssets, 0);
    const finalRound = rounds[3];
    const totalProfitAfter4Rounds = totalAssetsFromRounds + finalRound.cumulativeWithdrawals + amount;
    const totalProfit = totalProfitAfter4Rounds - amount;
    const totalROI = (totalProfit / amount) * 100;

    return {
      initialTicketOptimization,
      rounds,
      finalResults: {
        totalInvestment: amount,
        totalCapitalAllocated: initialTicketOptimization.totalCapital,
        totalProfitAfter4Rounds: totalProfitAfter4Rounds,
        totalROI: totalROI,
        totalWithdrawals: finalRound?.cumulativeWithdrawals || 0,
        totalBotProfit: rounds.reduce((sum, round) => sum + round.botProfit, 0)
      }
    };
  }, [investmentAmount]);

  const handleQuickAmount = (amount: number) => {
    setInvestmentAmount(amount.toString());
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Strategy Explanation */}
      <StrategyExplanation />

      {/* Input Section - Mobile Optimized */}
      <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Nhập Số Tiền Đầu Tư</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Khám phá tiềm năng lợi nhuận theo mô hình 1-3-6</p>
        </div>
        
        <div className="max-w-md mx-auto space-y-4 sm:space-y-6">
          <Label htmlFor="investment" className="text-base sm:text-lg font-semibold text-foreground block text-center">
            Số tiền đầu tư (USD)
          </Label>
          
          {/* Main Input */}
          <div className="relative">
            <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-xl sm:text-2xl text-primary font-bold">$</span>
            <Input
              id="investment"
              type="number"
              value={investmentAmount}
              onChange={(e) => setInvestmentAmount(e.target.value)}
              placeholder="Nhập số tiền đầu tư..."
              className="text-xl sm:text-2xl font-bold pl-10 sm:pl-12 pr-4 py-4 sm:py-6 bg-background border-border text-foreground placeholder:text-muted-foreground rounded-2xl focus:border-primary transition-all duration-300 min-h-[56px] sm:min-h-[64px]"
            />
          </div>

          {/* Quick Amount Buttons - Mobile Optimized */}
          <div className="space-y-3">
            <div className="text-center text-sm text-muted-foreground">
              Chọn nhanh:
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {QUICK_AMOUNTS.map((amount) => (
                <Button
                  key={amount}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAmount(amount)}
                  className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3 hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  ${amount.toLocaleString()}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Info Cards - Mobile Optimized */}
          <div className="space-y-3">
            <div className="text-center space-y-2">
              <p className="text-xs sm:text-sm text-muted-foreground bg-secondary p-3 rounded-xl">
                💡 Ví dụ: $2,000 sẽ mua được vé $1,249 + vé $649 (dư $102)
              </p>
              <p className="text-xs text-primary bg-secondary p-2 rounded-lg">
                ⚡ Mô hình 1-3-6: AI Prop thi 2 tháng → Tháng 3 có lợi nhuận → Bot giao dịch tích lũy
              </p>
            </div>
          </div>
        </div>
      </div>

      {calculationResults && (
        <div className="space-y-6 sm:space-y-8">
          {/* Initial Ticket Optimization */}
          <TicketOptimizer optimization={calculationResults.initialTicketOptimization} />

          {/* Final Results */}
          <ResultsDisplay results={calculationResults.finalResults} />

          {/* Round Calculations */}
          <RoundCalculations rounds={calculationResults.rounds} />
        </div>
      )}
    </div>
  );
};
